// Editor for customer.featureFlags.eventCategories — list of { keyword, color } pairs.
// When a calendar event title fuzzy-matches a keyword, the event is auto-colored with
// the matching color (picked from the global named COLOR_PICKER_LIST).
import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text, withText } from 'preact-i18n';

import ColorPicker from '../gui/colorPicker';
import { COLOR_PICKER_LIST } from '../../lib/fields';

@withText(props => ({
    keywordPlaceholder: <Text id='customer.eventCategory-keywordPlaceholder'>Title keyword (e.g. Operasjon)</Text>,
}))
@observer
class EventCategoriesEditor extends Component {
    constructor(props) {
        super(props);
        this.state = { saving: false };
        this.saveTimer = null;
    }

    componentWillUnmount() {
        if (this.saveTimer) {
            clearTimeout(this.saveTimer);
        }
    }

    getCustomer = () => {
        const { isNew } = this.props;
        const { customerStore } = this.props.stores;
        return isNew ? customerStore.newCustomer : customerStore.customer;
    }

    getCategories = () => {
        const customer = this.getCustomer();
        return customer?.featureFlags?.eventCategories || [];
    }

    getColorName = (hex) => {
        if (!hex) return '';
        const match = COLOR_PICKER_LIST.find(([h]) => h.toLowerCase() === hex.toLowerCase());
        return match ? match[1] : hex;
    }

    persist = (next) => {
        const { isNew } = this.props;
        const { customerStore, userStore, appState } = this.props.stores;
        const customer = this.getCustomer();
        const objectName = isNew ? 'newCustomer' : 'customer';
        // Update locally for immediate UI feedback
        customerStore.updateObjectKeyValue(objectName, 'featureFlags', {
            ...(customer?.featureFlags || {}),
            eventCategories: next,
        });
        // Mirror to userStore.customer and appState.currentCustomer when they reference the
        // same customer, so the calendar grid sees new categories without a page refresh.
        if (userStore?.customer && customer?.id && userStore.customer.id === customer.id) {
            userStore.updateObjectKeyValue('customer', 'featureFlags', {
                ...(userStore.customer.featureFlags || {}),
                eventCategories: next,
            });
        }
        if (appState?.currentCustomer && customer?.id && appState.currentCustomer.id === customer.id) {
            appState.updateObjectKeyValue('currentCustomer', 'featureFlags', {
                ...(appState.currentCustomer.featureFlags || {}),
                eventCategories: next,
            });
        }
        if (isNew || !customer?.id) return;
        if (this.saveTimer) clearTimeout(this.saveTimer);
        this.setState({ saving: true });
        this.saveTimer = setTimeout(async () => {
            await customerStore.saveField(customer.id, 'featureFlags.eventCategories', next, true);
            this.setState({ saving: false });
        }, 750);
    }

    addCategory = () => {
        const categories = this.getCategories();
        const used = new Set(categories.map(c => (c.color || '').toLowerCase()));
        const nextColor = COLOR_PICKER_LIST.find(([hex]) => !used.has(hex.toLowerCase()))?.[0]
            || COLOR_PICKER_LIST[0][0];
        this.persist([...categories, { keyword: '', color: nextColor }]);
    }

    removeCategory = (idx) => {
        const categories = this.getCategories();
        this.persist(categories.filter((_, i) => i !== idx));
    }

    updateKeyword = (idx, keyword) => {
        const categories = this.getCategories();
        this.persist(categories.map((c, i) => (i === idx ? { ...c, keyword } : c)));
    }

    updateColor = (idx, color) => {
        const categories = this.getCategories();
        this.persist(categories.map((c, i) => (i === idx ? { ...c, color } : c)));
    }

    render() {
        const { keywordPlaceholder } = this.props;
        const { saving } = this.state;
        const categories = this.getCategories();

        return (
            <div class='form-group my-0 py-1'>
                <label class='text-muted ms-2' style='font-size: 0.75em;'>
                    <Text id='customer.eventCategories'>Event categories</Text>
                </label>
                <small class='form-text text-body-secondary px-2 mt-0 mb-2 d-block'>
                    <Text id='customer.eventCategories-help'>
                        When a calendar event title contains one of these keywords, the event is automatically colored with the matching color.
                    </Text>
                    {' '}
                    <Text id='customer.eventCategories-commaHint'>
                        Separate several keywords with a comma, e.g. Operasjon, Anestesi.
                    </Text>
                </small>
                {categories.length === 0 && (
                    <div class='alert alert-light text-body-secondary py-2 mx-2'>
                        <Text id='customer.eventCategories-empty'>No categories yet. Add one to get started.</Text>
                    </div>
                )}
                <div class='d-flex flex-column gap-2 mx-2'>
                    {categories.map((cat, idx) => (
                        <div key={`cat-${idx}`} class='d-flex flex-column border rounded p-2'>
                            <div class='d-flex align-items-center gap-2'>
                                <span
                                    class='rounded-circle border flex-shrink-0'
                                    style={`width: 24px; height: 24px; background-color: ${cat.color || '#ffffff'};`}
                                    title={this.getColorName(cat.color)}
                                    aria-hidden='true'
                                />
                                <input
                                    type='text'
                                    class='form-control form-control-sm flex-grow-1'
                                    value={cat.keyword || ''}
                                    placeholder={keywordPlaceholder}
                                    onInput={e => this.updateKeyword(idx, e.target.value)}
                                />
                                <small class='text-body-secondary text-nowrap d-none d-md-inline'>
                                    {this.getColorName(cat.color)}
                                </small>
                                <button
                                    type='button'
                                    class='btn btn-sm btn-outline-danger'
                                    onClick={() => this.removeCategory(idx)}
                                    aria-label='Remove'
                                >
                                    <i class='fa-solid fa-trash' />
                                </button>
                            </div>
                            <div class='mt-2'>
                                <ColorPicker
                                    key={`cp-${idx}-${cat.color || 'none'}`}
                                    options={COLOR_PICKER_LIST}
                                    value={cat.color}
                                    onChange={hex => this.updateColor(idx, hex)}
                                    label='Choose category color'
                                />
                            </div>
                        </div>
                    ))}
                </div>
                <div class='mt-2 mx-2 d-flex align-items-center gap-2'>
                    <button type='button' class='btn btn-sm btn-outline-primary' onClick={this.addCategory}>
                        <i class='fa-solid fa-plus me-1' />
                        <Text id='customer.eventCategories-add'>Add category</Text>
                    </button>
                    {saving && (
                        <small class='text-body-secondary'>
                            <i class='fa-solid fa-circle-notch fa-spin me-1' />
                            <Text id='customer.eventCategories-saving'>Saving...</Text>
                        </small>
                    )}
                </div>
            </div>
        );
    }
}

export default EventCategoriesEditor;
